// src/components/Budget/BudgetInsightTip.js
// AI-powered inline tip that appears below each budget category card.
// Uses client-side heuristics (no API call) to surface actionable advice.

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/helpers';

// ──────────────────────────────────────────────
// Heuristic tip generator
// ──────────────────────────────────────────────
const generateTip = (spent, budget, daysRemaining, daysInMonth, categoryName, t) => {
  if (budget <= 0) return null; // can't give useful advice without a positive budget

  const percentage = (spent / budget) * 100;
  const remaining = budget - spent;
  const daysPassed = daysInMonth - daysRemaining;
  const monthProgress = daysInMonth > 0 ? (daysPassed / daysInMonth) * 100 : 0;

  // 1. Already over budget
  if (spent > budget) {
    const overAmount = spent - budget;
    return {
      type: 'over',
      severity: 'error',
      message: t(
        'budgetTips.overBudget',
        `You've exceeded your {{category}} budget by {{amount}}. Consider adjusting your target or reducing spending for the rest of the month.`,
        { category: categoryName, amount: formatCurrency(overAmount) }
      ),
      trend: t(
        'budgetTips.overBudgetTrend',
        'Review whether this budget reflects your real needs — increasing it may give a more accurate picture.'
      ),
    };
  }

  // 2. Danger zone: > 80% spent with > 7 days left
  if (percentage > 80 && daysRemaining > 7) {
    const dailyBudget = daysRemaining > 0 ? remaining / daysRemaining : 0;
    return {
      type: 'warning',
      severity: 'warning',
      message: t(
        'budgetTips.highSpending',
        'You\'ve used {{pct}}% of your {{category}} budget with {{days}} days left. Try limiting spending to {{perDay}}/day to stay on track.',
        {
          pct: Math.round(percentage),
          category: categoryName,
          days: daysRemaining,
          perDay: formatCurrency(dailyBudget),
        }
      ),
      trend: null,
    };
  }

  // 3. Doing great: < 30% spent with > 50% of month gone
  if (percentage < 30 && monthProgress > 50) {
    return {
      type: 'success',
      severity: 'success',
      message: t(
        'budgetTips.underBudget',
        'Great job! You\'ve only spent {{pct}}% of your {{category}} budget halfway through the month.',
        { pct: Math.round(percentage), category: categoryName }
      ),
      trend: t(
        'budgetTips.underBudgetTrend',
        'At this pace you could save {{amount}} this month.',
        { amount: formatCurrency(remaining) }
      ),
    };
  }

  // 4. Moderate: 50-80% spent with a reasonable amount of days left
  if (percentage >= 50 && percentage <= 80 && daysRemaining > 3) {
    const dailyBudget = daysRemaining > 0 ? remaining / daysRemaining : 0;
    return {
      type: 'info',
      severity: 'info',
      message: t(
        'budgetTips.moderate',
        'You have {{amount}} left for {{category}} — roughly {{perDay}}/day for the next {{days}} days.',
        {
          amount: formatCurrency(remaining),
          category: categoryName,
          perDay: formatCurrency(dailyBudget),
          days: daysRemaining,
        }
      ),
      trend: null,
    };
  }

  return null; // nothing noteworthy
};

// ──────────────────────────────────────────────
// Accent colours per severity
// ──────────────────────────────────────────────
const ACCENT = {
  error:   { color: '#ef4444', bg: '#ef4444' },
  warning: { color: '#f59e0b', bg: '#f59e0b' },
  success: { color: '#10b981', bg: '#10b981' },
  info:    { color: '#8b5cf6', bg: '#8b5cf6' }, // purple AI accent
};

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
const BudgetInsightTip = ({
  spent,
  budget,
  daysRemaining,
  daysInMonth,
  categoryName,
}) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const tip = useMemo(
    () => generateTip(spent, budget, daysRemaining, daysInMonth, categoryName, t),
    [spent, budget, daysRemaining, daysInMonth, categoryName, t]
  );

  if (!tip) return null;

  const accent = ACCENT[tip.severity] || ACCENT.info;

  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: alpha(accent.bg, theme.palette.mode === 'dark' ? 0.08 : 0.05),
        border: `1px solid ${alpha(accent.color, 0.18)}`,
        transition: 'all 0.3s ease',
      }}
    >
      {/* ── Collapsed header row ── */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            bgcolor: alpha(accent.bg, theme.palette.mode === 'dark' ? 0.14 : 0.09),
          },
        }}
      >
        <LightbulbIcon
          sx={{ fontSize: '1rem', color: '#f59e0b' }} // amber for lightbulb
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '0.7rem',
            color: accent.color,
            letterSpacing: 0.3,
          }}
        >
          {t('budgetTips.aiTipAvailable', 'AI Tip available')}
        </Typography>
        <IconButton
          size="small"
          sx={{
            p: 0.25,
            transition: 'transform 0.3s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            color: accent.color,
          }}
          aria-label={expanded ? 'Collapse tip' : 'Expand tip'}
        >
          <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>

      {/* ── Expanded content ── */}
      <Collapse in={expanded} timeout={300}>
        <Box sx={{ px: 1.5, pb: 1.25, pt: 0.25 }}>
          {/* Main tip message */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.78rem',
              lineHeight: 1.5,
              color: 'text.primary',
              mb: tip.trend ? 1 : 0.5,
            }}
          >
            {tip.message}
          </Typography>

          {/* Optional trend / secondary insight */}
          {tip.trend && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.7rem',
                lineHeight: 1.45,
                color: 'text.secondary',
                fontStyle: 'italic',
                mb: 0.75,
              }}
            >
              {tip.trend}
            </Typography>
          )}

          {/* "Powered by AI" badge */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important', color: '#8b5cf6 !important' }} />}
              label={t('budgetTips.poweredByAI', 'Powered by AI')}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontWeight: 500,
                borderColor: alpha('#8b5cf6', 0.3),
                color: '#8b5cf6',
                '& .MuiChip-icon': { ml: '4px' },
              }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default BudgetInsightTip;
